import { PaginationCardMinimal } from "@admin-ui/components/application/pagination/pagination";
import { TableEmptyState } from "@admin-ui/components/composed/table-empty-state";
import { ConfirmationDialog } from "@admin-ui/components/primitives/application/confirmation-dialog";
import { Table, TableCard } from "@admin-ui/components/primitives/application/table/table";
import { Badge } from "@admin-ui/components/primitives/base/badges/badges";
import { Dropdown } from "@admin-ui/components/primitives/base/dropdown/dropdown";
import { Input } from "@admin-ui/components/primitives/base/input/input";
import { formatDate } from "@admin-ui/utils/format-date";
import { toSortDescriptor } from "@admin-ui/utils/sort";
import { editAdminDestinationPath } from "@travily/routes";
import type { AdminDestinationsDestination } from "@travily/types/models";
import type AdminDestinationsIndex from "@travily/types/pages/AdminDestinationsIndex";
import { Edit05, MarkerPin01, SearchLg, Trash01 } from "@untitledui/icons";
import { useEffect, useState } from "react";
import type { SortDescriptor } from "react-aria-components";
import { Link, TableBody } from "react-aria-components";

export type DestinationFilters = AdminDestinationsIndex["filters"];
export type DestinationPagy = AdminDestinationsIndex["pagy"];

export interface IndexViewProps {
  destinations: AdminDestinationsDestination[];
  pagy: DestinationPagy;
  sort: string | null;
  sortDir: string | null;
  filters: DestinationFilters;
  onSortChange: (descriptor: SortDescriptor) => void;
  onPageChange: (page: number) => void;
  onSearchChange: (value: string) => void;
  onEditDestination: (destinationId: number) => void;
  onDeleteDestination: (destinationId: number) => void;
}

export function IndexView({
  destinations,
  pagy,
  sort,
  sortDir,
  filters,
  onSortChange,
  onPageChange,
  onSearchChange,
  onEditDestination,
  onDeleteDestination,
}: IndexViewProps) {
  const sortDescriptor = toSortDescriptor(sort, sortDir);

  const [searchDraft, setSearchDraft] = useState(filters.search);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
  const pendingDestination = pendingDeleteId ? destinations.find((d) => d.id === pendingDeleteId) : null;

  useEffect(() => {
    setSearchDraft(filters.search);
  }, [filters.search]);

  useEffect(() => {
    if (searchDraft === filters.search) return;
    const timer = setTimeout(() => onSearchChange(searchDraft), 300);
    return () => clearTimeout(timer);
  }, [searchDraft, filters.search, onSearchChange]);

  return (
    <>
      <div className="flex flex-col gap-3 rounded-xl bg-secondary p-4 ring-1 ring-secondary ring-inset md:flex-row md:flex-wrap md:items-end md:p-5">
        <div className="md:flex-1 md:min-w-64">
          <Input
            aria-label="Search destinations"
            placeholder="Search by name or country"
            icon={SearchLg}
            value={searchDraft}
            onChange={setSearchDraft}
            size="sm"
          />
        </div>
      </div>

      <TableCard.Root size="sm">
        <Table aria-label="Destinations" sortDescriptor={sortDescriptor} onSortChange={onSortChange}>
          <Table.Header>
            <Table.Head id="name" label="Name" isRowHeader allowsSorting className="w-full max-w-1/3" />
            <Table.Head id="country_code" label="Country" allowsSorting />
            <Table.Head id="locations" label="Locations" />
            <Table.Head id="description" label="Description" />
            <Table.Head id="created_at" label="Created" allowsSorting />
            <Table.Head id="actions" label="" />
          </Table.Header>
          <TableBody items={destinations} renderEmptyState={() => <EmptyState />}>
            {(destination: AdminDestinationsDestination) => (
              <Table.Row key={destination.id} id={destination.id}>
                <Table.Cell>
                  <Link
                    href={editAdminDestinationPath(destination.id)}
                    className="text-sm font-medium text-brand-600 hover:text-brand-700"
                  >
                    {destination.name}
                  </Link>
                </Table.Cell>
                <Table.Cell>
                  <Badge color="gray" size="sm">
                    {destination.country_code}
                  </Badge>
                </Table.Cell>
                <Table.Cell>{destination.location_count}</Table.Cell>
                <Table.Cell>
                  <span className="max-w-xs truncate text-sm text-tertiary">{destination.description ?? "-"}</span>
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap">{formatDate(destination.created_at)}</Table.Cell>
                <Table.Cell>
                  <Dropdown.Root>
                    <Dropdown.DotsButton />
                    <Dropdown.Popover className="w-min">
                      <Dropdown.Menu
                        onAction={(key) => {
                          if (key === "edit") onEditDestination(destination.id);
                          if (key === "delete") setPendingDeleteId(destination.id);
                        }}
                      >
                        <Dropdown.Item id="edit" icon={Edit05} label="Edit" />
                        <Dropdown.Item id="delete" icon={Trash01} label="Delete" />
                      </Dropdown.Menu>
                    </Dropdown.Popover>
                  </Dropdown.Root>
                </Table.Cell>
              </Table.Row>
            )}
          </TableBody>
        </Table>
      </TableCard.Root>

      <PaginationCardMinimal align="right" page={pagy.page} total={pagy.pages} onPageChange={onPageChange} />

      {pendingDestination && (
        <ConfirmationDialog
          title="Delete destination"
          description={`This will permanently delete "${pendingDestination.name}" and unlink all its locations. This action cannot be undone.`}
          confirmLabel="Delete"
          color="error"
          isOpen
          onOpenChange={(open) => {
            if (!open) setPendingDeleteId(null);
          }}
          onConfirm={() => {
            onDeleteDestination(pendingDestination.id);
            setPendingDeleteId(null);
          }}
        />
      )}
    </>
  );
}

function EmptyState() {
  return <TableEmptyState icon={MarkerPin01} title="No destinations match" description="Try clearing the search." />;
}
